import React from 'react';

import AliceCarousel, { EventObject } from 'react-alice-carousel';
import 'react-alice-carousel/lib/alice-carousel.css';

import { Language, Video } from '../domain/types';

const breakPoints = {
  0: {
    items: 1,
    itemsFit: 'contain'
  },
  640: {
    items: 2,
    itemsFit: 'contain'
  },
  900: {
    items: 3,
    itemsFit: 'contain'
  },
  1200: {
    items: 4,
    itemsFit: 'contain'
  },
  1440: {
    items: 5,
    itemsFit: 'contain'
  },
}

interface Props {
  title: string;
  videos: {video: Video}[];
  setFeatured: (video: Video) => void;
  language: Language;
  searchActive: boolean;
  emptySearchMessage?: string;
  emptyTrackMinHeight: number;
  sharedHeightTrackRef?: React.Ref<HTMLDivElement | null>;
}

interface PrevNextButtonProps {
  isPrev?: boolean;
  setHoveredControl: (control: 'prev' | 'next' | null) => void;
  hoveredControl: 'prev' | 'next' | null;
  onNav: (isPrev?: boolean) => void;
  totalItems: number;
  currentIndex: number;
  numVisible: number;
}

const PrevNextButton: React.FC<PrevNextButtonProps> = ({
  isPrev,
  setHoveredControl,
  hoveredControl,
  onNav,
  totalItems,
  currentIndex,
  numVisible
}) => {

  const isActive = isPrev ? hoveredControl === 'prev' : hoveredControl === 'next';

  if (totalItems <= numVisible) {
    return null;
  } else if (isPrev && currentIndex === 0) {
    return null;
  } else if (!isPrev && currentIndex > totalItems - numVisible - 1) {
    return null;
  }

  return (
    <div 
      onClick={() => onNav(isPrev)}
      onMouseEnter={() => setHoveredControl(isPrev ? 'prev' : 'next')}
      onMouseLeave={() => setHoveredControl(null)}
      style={{
        position: 'absolute',
        color: '#DB2600',
        top: 50,
        left: isPrev ? 0 : undefined,
        right: isPrev ? undefined : 0,
        zIndex: 10000,
        width: 60,
        height: 60,
        borderRadius: 30,
        backgroundColor: isActive ? 'white' : '#FFFFFF33',
        transition: 'background-color 0.5s ease',
        fontSize: 60,
        lineHeight: '50px',
        textAlign: 'center',
        cursor: 'pointer',
      }}
    >
      {isPrev ? (
      <span>
        &#8249;
      </span>
      ) : (
      <span>
        &#8250;
      </span>
      )}
    </div>
  );
};

const getLocalizedField = (video: Video, prefix: string, lang: Language): string | null => {
  const key = `${prefix}_${lang}` as keyof Video;
  return (video[key] as string | null) ?? null;
};

const hasMetadata = (video: Video): boolean => {
  return !!(
    video.producer_ensemble ||
    video.producer_text_nl || video.producer_text_en ||
    video.description_nl || video.description_en ||
    video.handwriting_url
  );
};


const VideoCarousel: React.FC<Props> = ({
  title,
  videos,
  setFeatured,
  language,
  searchActive,
  emptySearchMessage,
  emptyTrackMinHeight,
  sharedHeightTrackRef,
}) => {

  const visibleVideos = React.useMemo(() => {
    return videos.filter(vid => {
      if (!vid.video.available_date_time) return true;
      const now = new Date();
      const date = new Date(vid.video.available_date_time);
      return now > date;
    });
  }, [videos]);

  const ref = React.useRef<AliceCarousel>(null);

  const [activeIndex, setActiveIndex] = React.useState<number>();
  const [numVisible, setNumVisible] = React.useState<number>();

  const [hoveredControl, setHoveredControl] = React.useState<'prev' | 'next' | null>(null);

  const handleDragStart = React.useCallback((e: any) => e.preventDefault(), []);

  const onNext = React.useCallback(() => {
    ref.current?.slideNext();
  }, []);

  const onPrev = React.useCallback(() => {
    ref.current?.slidePrev();
  }, []);

  const onClick = React.useCallback((v: Video) => {
    setFeatured(v);
  }, [setFeatured]);

  const onUpdate = React.useCallback((event: EventObject) => {
    setNumVisible(event.itemsInSlide);
    setActiveIndex(event.item);
  }, []);

  const items = React.useMemo(() => visibleVideos.map((vid) => {
    const video = vid.video;
    const showOverlay = hasMetadata(video);

    const producer = video.producer_ensemble?.name
      ?? getLocalizedField(video, 'producer_text', language);
    const description = getLocalizedField(video, 'description', language);
    const handwritingTitle = getLocalizedField(video, 'handwriting_title', language);

    return (
      <div
        key={video.pk}
        className={showOverlay ? 'video-card' : undefined}
        style={{paddingLeft: 10, paddingRight: 10, overflow: 'hidden', position: 'relative'}}
        onDragStart={handleDragStart}
        onClick={() => onClick(video)}
        role="button"
        tabIndex={0}
        onTouchEnd={(e) => {
          e.preventDefault();
          onClick(video);
        }}
      >
        <img
          className='video-carousel-img'
          style={{
            objectFit: 'contain', 
            width: '100%', 
            cursor: 'pointer', 
            transition: 'transform 0.5s ease',
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            userSelect: 'none',
            display: 'block',
          }}
          src={video.placeholder_image}
          alt={`${video.title_en} placeholder`}
          draggable={false}
        />
        {showOverlay && (
          <div className="video-card-overlay">
            {producer && (
              <div className="video-card-producer">{producer}</div>
            )}
            {description && (
              <div
                className="video-card-description"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
            {handwritingTitle && (
              <div className="video-card-handwriting">{handwritingTitle}</div>
            )}
          </div>
        )}
      </div>
    );
  }), [visibleVideos, onClick, handleDragStart, language]);

  const showSearchEmpty =
    searchActive && videos.length === 0 && !!emptySearchMessage;

  const emptyMinHeightPx =
    emptyTrackMinHeight > 0 ? emptyTrackMinHeight : undefined;

  return (
    <>
    <div className="video-carousel-outer">
      <h3 className="video-carousel-title">
        {title}
      </h3>
      {showSearchEmpty ? (
        <div
          className="video-carousel-empty"
          role="status"
          style={
            emptyMinHeightPx !== undefined
              ? { minHeight: emptyMinHeightPx }
              : undefined
          }
        >
          <p className="video-carousel-empty-message">
            {emptySearchMessage}
          </p>
        </div>
      ) : (
      <div
        ref={
          sharedHeightTrackRef as React.LegacyRef<HTMLDivElement> | undefined
        }
        className="video-carousel-track"
      >
        <AliceCarousel
          ref={ref}
          mouseTracking
          touchTracking
          touchMoveDefaultEvents={false}
          items={items}
          renderDotsItem={() => null}
          responsive={breakPoints}
          onInitialized={onUpdate}
          onSlideChanged={onUpdate}
          renderNextButton={() => (
            <PrevNextButton
              setHoveredControl={setHoveredControl}
              hoveredControl={hoveredControl}
              onNav={onNext}
              totalItems={visibleVideos.length}
              currentIndex={activeIndex ?? 0}
              numVisible={numVisible ?? 0}
            />            
          )}
          renderPrevButton={() => (
            <PrevNextButton
              isPrev
              setHoveredControl={setHoveredControl}
              hoveredControl={hoveredControl}
              onNav={onPrev}
              totalItems={visibleVideos.length}
              currentIndex={activeIndex ?? 0}
              numVisible={numVisible ?? 0}
            />
          )}
        />
      </div>
      )}
    </div>
    </>
  );
};

export default React.memo(VideoCarousel);